// React / Redux / Related
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { I18n, Translate } from 'react-redux-i18n';
import { useNavigate, useParams } from 'react-router-dom';
import { withRouterV6 } from '../../helpers/withRouterV6.jsx';

// Actions
import * as eventActions from './store/eventActions';

// Components
import BasePage from '../../components/common/basePageView';
import clear from '../../components/clear';
import Grid from '../../components/common/grid';
import ContextMenu from '../../components/common/grid/contextMenu';
import Panel from '../../components/common/grid/managed/panel';
import Button from '../../components/common/button';
import { Common } from '../../components';
import { Menu } from 'semantic-ui-react';
import RecordResultMessage from '../../components/common/recordResult';
import Spinner from '../../components/common/spinner';
import { ExportDownloadProvider } from '../export-history';
// Backoffice modal removed

// Constants
import { gridKeys } from '../../constants/gridKeys';
import { i18nKeys } from './constants';
import { CONTEXT_MENU_KEYS } from '../../constants/contextMenuEntries';

// Helpers
import get from 'lodash.get';
import { Record } from '../../modules/bin/utility';
import { IEvent } from '../../models/Event';
import { withPageTitle } from '../../hocs';
import GridNavigationHelper from '../../helpers/GridNavigationHelper';
import { WriteOnly } from '../../components/common/grid/WriteOnly.js';
import { renderFranchiseAwareNameColumn } from '../../helpers/franchiseNameFormatter';
import {
  isGiftAidColumnHidden,
  selectCanSeeGiftAid
} from '../../helpers/giftAidHelper';
import { selectAreFeatureFlagsSettled } from '../../modules/featureFlags/featureFlagSelectors';

// ----------------------------------------------------------------------

interface EventPageProps {
  history: any; // Using any for withRouter compatibility
  isEventAdmin: boolean;
  isEventOrganizerAdmin: boolean;
  isSystemAdmin: boolean;
  isSessionLoading: boolean;
  isExportDataAllowed: boolean;
  areFeatureFlagsSettled: boolean;
  canSeeGiftAid: boolean;
  list: any;
  actionGrid: any;
  impersonatedRecord: any;
  createAccountRecord: any;
  recordEdit: any;
  recordCreate: any;
  match: any;
}

interface IEventPageState {}

/** A page to view all events in a list */
class EventPage extends Component<EventPageProps, IEventPageState> {
  constructor(props: EventPageProps | Readonly<EventPageProps>) {
    super(props);
  this.state = {} as any;
  }

  componentDidMount() {
    // Clear any stored return URL when entering this grid
    GridNavigationHelper.clearReturnUrlOnGridEntry();
  }

  onCreateClick = () => {
    this.props.history.push('/events/create/setup');
  };

  isChooseTabButtonDisabled = () => {
    return (
      Record.isRecordLoading(this.props.list) ||
      Record.isRecordLoading(this.props.list.metadata)
    );
  };

  getPanel = () => {
    const canCreate =
      !this.props.isEventAdmin && !this.props.isEventOrganizerAdmin;

    if (!canCreate && !this.props.isExportDataAllowed) {
      return <React.Fragment />;
    }

    return (
      <Panel name="right">
        {this.props.isExportDataAllowed && (
          <ExportDownloadProvider entity="page/event">
            {({ open }) => (
              <Button
                id="btnExportEvents"
                onClick={() => open(this.props.list.request)}
              >
                <Translate value={i18nKeys.EXPORT_BUTTON} />
              </Button>
            )}
          </ExportDownloadProvider>
        )}
        {canCreate && (
          <WriteOnly>
            <Button id="btnCreateEvent" primary onClick={this.onCreateClick}>
              <Translate value={i18nKeys.CREATE_BUTTON} />
            </Button>
          </WriteOnly>
        )}
      </Panel>
    );
  };

  isEditContextMenuItemHidden = () => {
    return this.props.isEventAdmin || this.props.isEventOrganizerAdmin;
  };

  isDonationsContextMenuItemHidden = () => {
    return this.props.isEventAdmin || this.props.isEventOrganizerAdmin;
  };

  isFunraiserContextMenuHidden = () => {
    return this.props.isEventOrganizerAdmin;
  };

  isEventCloneHidden = () => {
    return this.props.isEventAdmin || this.props.isEventOrganizerAdmin;
  };

  isActiveIntegrationMenuHiddenn = (item: any) => {
    return (
      !this.props.isSystemAdmin ||
      item?.external?.system !== 'active' ||
      item?.status !== 'live'
    );
  };

  // Backoffice modal removed

  getContextMenu = () => {
    return (
      <ContextMenu>
        <ContextMenu.Item
          key={CONTEXT_MENU_KEYS.EVENTS.VIEW_PERFORMANCE.key}
          label={I18n.t(i18nKeys.DASHBOARD_ACTION)}
          contextMenuOptions={CONTEXT_MENU_KEYS.EVENTS.VIEW_PERFORMANCE}
        />
        <ContextMenu.Item
          key={CONTEXT_MENU_KEYS.EVENTS.EDIT.key}
          label={I18n.t(i18nKeys.EDIT_ACTION)}
          contextMenuOptions={CONTEXT_MENU_KEYS.EVENTS.EDIT}
          onClick={GridNavigationHelper.createEditClickHandler(null, null)}
          isHiden={this.isEditContextMenuItemHidden}
        />
        <ContextMenu.Item
          key={CONTEXT_MENU_KEYS.EVENTS.VIEW.key}
          label={I18n.t(i18nKeys.VIEW_ACTION)}
          contextMenuOptions={CONTEXT_MENU_KEYS.EVENTS.VIEW}
        />
        <ContextMenu.Item
          key={CONTEXT_MENU_KEYS.EVENTS.VIEW_ORGANIZATIONS.key}
          label={I18n.t(i18nKeys.ORGANIZATIONS_ACTION)}
          contextMenuOptions={CONTEXT_MENU_KEYS.EVENTS.VIEW_ORGANIZATIONS}
        />
        <ContextMenu.Item
          key={CONTEXT_MENU_KEYS.EVENTS.VIEW_DONATIONS.key}
          label={I18n.t(i18nKeys.DONATIONS_ACTION)}
          contextMenuOptions={CONTEXT_MENU_KEYS.EVENTS.VIEW_DONATIONS}
          isHiden={this.isDonationsContextMenuItemHidden}
        />
        <ContextMenu.Item
          key={CONTEXT_MENU_KEYS.EVENTS.VIEW_FUNDRAISERS.key}
          label={I18n.t(i18nKeys.FUNDRAISERS_ACTION)}
          contextMenuOptions={CONTEXT_MENU_KEYS.EVENTS.VIEW_FUNDRAISERS}
          isHiden={this.isFunraiserContextMenuHidden}
        />
        <ContextMenu.Item
          key={CONTEXT_MENU_KEYS.EVENTS.ACTIVE_INTEGRATION.key}
          label={I18n.t(i18nKeys.ACTIVE_INTEGRATION)}
          contextMenuOptions={CONTEXT_MENU_KEYS.EVENTS.ACTIVE_INTEGRATION}
          isHiden={this.isActiveIntegrationMenuHiddenn}
          systemOnly
        />
        <ContextMenu.Item
          key={CONTEXT_MENU_KEYS.EVENTS.CLONE_EVENT.key}
          label={I18n.t(i18nKeys.CLONE_ACTION)}
          contextMenuOptions={CONTEXT_MENU_KEYS.EVENTS.CLONE_EVENT}
          isHiden={this.isEventCloneHidden}
        />
        <ContextMenu.Item
          key={CONTEXT_MENU_KEYS.EVENTS.BACK_OFFICE.key}
          label={I18n.t(i18nKeys.BACK_OFFICE)}
          onClick={(_id: string, item: IEvent) => this.props.history.push(`/events/${item.id}/backoffice`)}
          isHiden={() => !this.props.isSystemAdmin}
          systemOnly
        />
      </ContextMenu>
    );
  };

  hideColumnCondition = (item: { path?: string }) =>
    isGiftAidColumnHidden(item, this.props.canSeeGiftAid);

  clearMessages = () => {
    this.props.actionGrid.clearRecordMessages('recordEdit');
    this.props.actionGrid.clearRecordMessages('recordCreate');
  };

  // The Gift Aid column depends on the UK charity flag. The grid waits for the
  // flags so the column picker never drops a column it was not yet showing.
  showSpinner = () => {
    return (
      !this.props.areFeatureFlagsSettled ||
      this.props.isSessionLoading ||
      Record.isRecordLoading(this.props.createAccountRecord) ||
      Record.isRecordLoading(this.props.impersonatedRecord)
    );
    // || Record.isRecordLoading(this.props.exportFileRecord);
  };

  render() {
    if (this.showSpinner()) {
      return <Spinner />;
    }

    const panel = this.getPanel();
    const contextMenu = this.getContextMenu();
    const filterButtons = get(this.props, 'list.filterButtons');

    return (
      <>
        <BasePage>
          <RecordResultMessage
            record={this.props.recordEdit}
            onDismiss={this.clearMessages}
          />
          <RecordResultMessage
            record={this.props.recordCreate}
            onDismiss={this.clearMessages}
          />

          <h1>
            <Translate value={i18nKeys.PAGE_TITLE} />
          </h1>
          <Menu
            className="accounts-tab-buttons-group"
            style={{ margin: '0px', borderRight: '0px' }}
          >
            {Object.keys(filterButtons).map((key) => {
              const item = filterButtons[key];
              return (
                <Menu.Item
                  key={item.key}
                  name={item.key}
                  content={I18n.t(item.I18nKey)}
                  active={item.key === this.props.match.params.tab}
                  disabled={this.isChooseTabButtonDisabled()}
                  onClick={() => {
                    this.props.history.push(`/events/${item.key}`);
                  }}
                />
              );
            })}
          </Menu>
          <Grid.Managed
            listKey={gridKeys.EVENTS}
            list={this.props.list}
            actions={this.props.actionGrid}
            filtersButton
            search
            toggleColumns
            hideColumn={this.hideColumnCondition}
            activeTabFilter={this.props.match.params.tab}
            columnRender={renderFranchiseAwareNameColumn(this.props.isSystemAdmin)}
          >
            {panel}
            {contextMenu}
          </Grid.Managed>
        </BasePage>
  {/* Backoffice modal removed */}
      </>
    );
  }
}

/** Maps the state to properties */
const mapState = (state: any) => {
  const { event, authentication, account, session } = state;
  // @ts-ignore
  const stateGrid = Common.Grid.Managed.mapGridState(event, gridKeys.EVENTS);
  return {
    listKey: stateGrid.key,
    list: stateGrid.list,
    recordEdit: event.recordEdit,
    recordCreate: event.recordCreate,
    createAccountRecord: authentication.invite.createAccountRecord,
    impersonatedRecord: account.impersonatedRecord,
    isSessionLoading: session?.processing,
    isSystemAdmin: session?.isSystemAdmin,
    isEventAdmin: session?.isEventAdmin,
    isEventOrganizerAdmin: session?.isEventOrganizerAdmin,
    isExportDataAllowed: get(session, 'permissions.exportData') === 'allow',
    areFeatureFlagsSettled: selectAreFeatureFlagsSettled(state),
    canSeeGiftAid: selectCanSeeGiftAid(state)
  };
};

/** Maps the actions to properties */
const mapDispatch = (dispatch: any) => {
  var actions = {
    getMetadata: eventActions.getMetadata,
    getListData: eventActions.getListData,
    getListDataBySearchTerm: eventActions.getListDataBySearchTerm,
    toggleColumnsChange: eventActions.toggleColumnsChange,
    onGridTabChange: eventActions.onGridTabChange
  };

  // @ts-ignore
  const actionGrid = Grid.Managed.bindGridActions(dispatch, actions);

  return {
    actionGrid
  };
};

const titleResolver = (props: any) => {
  const tab = get(props, 'match.params.tab', 'live');
  const capitalizedTab = tab.charAt(0).toUpperCase() + tab.slice(1);
  return I18n.t('page-titles.events.list', { tab: capitalizedTab });
};

/** Connects component to Redux store */
// @ts-ignore: withRouter not available in v6, but needed for legacy class component
const EventPageContainer = withRouterV6(clear(
  connect(mapState, mapDispatch)(withPageTitle(titleResolver)(EventPage))
));
export default EventPageContainer;
